import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode } from '@lexical/rich-text';
import { ListNode, ListItemNode, INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $patchStyleText } from '@lexical/selection';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  FORMAT_TEXT_COMMAND,
  EditorState,
  LexicalEditor,
} from 'lexical';
import { Bold, Italic, List, ListOrdered, Underline } from 'lucide-react';
import { Box, Tooltip } from '@mui/material';
import './styles.css';

const theme = {
  paragraph: 'notesEditor__paragraph',
  text: {
    bold: 'notesEditor__textBold',
    italic: 'notesEditor__textItalic',
    underline: 'notesEditor__textUnderline',
  },
  list: {
    ul: 'notesEditor__ul',
    ol: 'notesEditor__ol',
    listitem: 'notesEditor__li',
  },
};

function InitialContentPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const content = html ?? '';
      if (!content.trim()) {
        root.append($createParagraphNode());
        return;
      }

      const looksLikeHtml = /<[a-z][\s\S]*>/i.test(content);
      if (looksLikeHtml) {
        const parser = new DOMParser();
        const dom = parser.parseFromString(content, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        if (nodes.length) {
          root.append(...nodes);
        } else {
          root.append($createParagraphNode());
        }
      } else {
        content.split('\n').forEach((line) => {
          const p = $createParagraphNode();
          p.append($createTextNode(line));
          root.append(p);
        });
      }
    });
  }, [editor, html]);

  return null;
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [formats, setFormats] = useState({ bold: false, italic: false, underline: false });

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setFormats({
            bold: selection.hasFormat('bold'),
            italic: selection.hasFormat('italic'),
            underline: selection.hasFormat('underline'),
          });
        }
      });
    });
  }, [editor]);

  const applyFontSize = (size: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { 'font-size': size });
      }
    });
  };

  const ToolBtn = ({
    title,
    active,
    onClick,
    children,
  }: {
    title: string;
    active?: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <Tooltip title={title} arrow placement="top">
      <Box
        component="button"
        type="button"
        onClick={onClick}
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        className={`notesToolbar__btn${active ? ' is-active' : ''}`}
      >
        {children}
      </Box>
    </Tooltip>
  );

  return (
    <Box className="notesToolbar">
      <ToolBtn
        title="Bold"
        active={formats.bold}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
      >
        <Bold size={14} strokeWidth={2.4} />
      </ToolBtn>
      <ToolBtn
        title="Italic"
        active={formats.italic}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
      >
        <Italic size={14} strokeWidth={2.4} />
      </ToolBtn>
      <ToolBtn
        title="Underline"
        active={formats.underline}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
      >
        <Underline size={14} strokeWidth={2.4} />
      </ToolBtn>

      <Box className="notesToolbar__divider" />

      <ToolBtn
        title="Bullet list"
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
      >
        <List size={14} strokeWidth={2.4} />
      </ToolBtn>
      <ToolBtn
        title="Numbered list"
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
      >
        <ListOrdered size={14} strokeWidth={2.4} />
      </ToolBtn>

      <Box className="notesToolbar__divider" />

      <select
        className="notesToolbar__select"
        defaultValue="14px"
        onChange={(e) => applyFontSize(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        title="Font size"
      >
        <option value="12px">12</option>
        <option value="14px">14</option>
        <option value="16px">16</option>
        <option value="18px">18</option>
        <option value="20px">20</option>
      </select>
    </Box>
  );
}

type NotesEditorProps = {
  initialHtml: string;
  onHtmlChange: (html: string) => void;
};

const NotesEditor = ({ initialHtml, onHtmlChange }: NotesEditorProps) => {
  const initialConfig = {
    namespace: 'MyNotesEditor',
    theme,
    onError: (error: Error) => console.error(error),
    nodes: [HeadingNode, ListNode, ListItemNode],
  };

  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor, null);
        onHtmlChange(html);
      });
    },
    [onHtmlChange],
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Box className="notesEditorRoot">
        <ToolbarPlugin />
        <Box className="notesEditorBody">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="notesEditor__content"
                aria-placeholder="Write your notes…"
                placeholder={
                  <Box className="notesEditor__placeholder">Write your notes…</Box>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </Box>
        <HistoryPlugin />
        <ListPlugin />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
        <InitialContentPlugin html={initialHtml} />
      </Box>
    </LexicalComposer>
  );
};

export default NotesEditor;
